import React, { useMemo, useState } from "react";
import "./ui.css";

/**
 * DataTable – sdílená tabulka s řízenými šířkami sloupců, řazením a mobilním
 * režimem (vodorovný posuv nebo karty). Nahrazuje per-tabulka ad-hoc šířky
 * a globální nth-child pravidla z App.css.
 *
 * Zásady šířek: textové sloupce pružné (`width: "flex"`, jméno nejširší),
 * čísla pevně na obsah (`numeric: true` = tabular-nums + zarovnání vpravo),
 * kategorie/stav střední pevná šířka (`width: "6rem"`), akce na obsah
 * (`width: "fit"`). Hlavičky se nikdy nezalamují.
 *
 * Čistě prezentační komponenta: jediný vnitřní stav je řazení (když není
 * řízené zvenčí). Řazení řeší komponenta sama (text / number / date, prázdné
 * hodnoty vždy na konci); `sortRows={false}` ho vypne, pokud řádky řadí rodič.
 *
 * Příklad:
 *
 *   type Member = { id: string; name: string; category: string; points: number; date: string };
 *
 *   <DataTable<Member>
 *     columns={[
 *       { key: "name", header: "Jméno", sortable: true, width: "flex" },
 *       // dva klíče řazení v jedné hlavičce (každý je vlastní tlačítko):
 *       // { key: "name", header: "Jméno", width: "flex",
 *       //   sortKeys: [{ key: "lastName", label: "Příjmení" }, { key: "firstName", label: "Jméno" }] },
 *       { key: "category", header: "Kat.", sortable: true, width: "6rem", align: "center" },
 *       { key: "points", header: "Body", sortable: true, sortType: "number", numeric: true },
 *       { key: "date", header: "Datum", sortable: true, sortType: "date", nowrap: true, hideBelow: "md" },
 *       { key: "actions", header: "", width: "fit", render: (m) => <button className="btn btn-sm btn-outline-primary">Upravit</button> },
 *     ]}
 *     rows={members}
 *     rowKey={(m) => m.id}
 *     defaultSort={{ key: "name", dir: "asc" }}
 *     onRowClick={(m) => open(m)}
 *     rowProps={(m) => (m.id === myId ? { className: "table-primary", title: "Vy" } : {})}
 *     mobile="cards"
 *     cardTitle={(m) => m.name}
 *     cardSubtitle={(m) => m.category}
 *     cardFields={["points", "date"]}
 *     cardActions={(m) => <button className="btn btn-sm btn-outline-primary">Upravit</button>}
 *     status={loading ? "loading" : members.length ? "idle" : "empty"}
 *     emptyText="Žádní členové"
 *   />
 *
 * Přilepená hlavička (`stickyHeader`) od 768 px vypíná vodorovný posuv vnitřního
 * obalu, proto patří tabulka do vlastního posuvného kontejneru, který nese i
 * odsazení `--lks-table-sticky-top` (viz prop):
 *
 *   <div style={{ maxHeight: "70vh", overflow: "auto", "--lks-table-sticky-top": "0px" } as React.CSSProperties}>
 *     <DataTable<Member> columns={columns} rows={members} rowKey={(m) => m.id} stickyHeader />
 *   </div>
 */

export type DataTableAlign = "left" | "center" | "right";
export type DataTableSortType = "text" | "number" | "date";
export type DataTableSortDir = "asc" | "desc";
export type DataTableBreakpoint = "sm" | "md" | "lg";
/** "flex" = bez šířky (vyplní zbytek), "fit" = na obsah (width 1 % + nowrap), jinak px/rem/%. */
export type DataTableWidth = "flex" | "fit" | string;
export type DataTableMobile = "scroll" | "cards";
export type DataTableStatus = "idle" | "loading" | "empty";

/** Jeden z více klíčů řazení v hlavičce sloupce (`sortKeys`). */
export interface DataTableSortKey<T> {
  /** Klíč řazení; bez `sortValue` se řadí `row[key]`. */
  key: string;
  /** Popisek tlačítka v hlavičce. */
  label: React.ReactNode;
  /** Title tlačítka (vysvětlivka). */
  title?: string;
  /** Typ řazení; výchozí podle sloupce. */
  sortType?: DataTableSortType;
  sortValue?: (row: T) => unknown;
}

export interface DataTableColumn<T> {
  /** Klíč sloupce; bez `render` se zobrazí `row[key]`. */
  key: string;
  header: React.ReactNode;
  /** Obsah buňky; index je pořadí řádku po seřazení. */
  render?: (row: T, index: number) => React.ReactNode;
  sortable?: boolean;
  /** Typ řazení. Výchozí "text" (nebo "number", je-li sloupec numeric). */
  sortType?: DataTableSortType;
  /** Hodnota pro řazení; bez ní se řadí `row[key]`. */
  sortValue?: (row: T) => unknown;
  /**
   * Více klíčů řazení v jedné hlavičce (např. Příjmení / Jméno): každý klíč je
   * vlastní tlačítko s indikátorem; `header` se pak v tabulce nezobrazí
   * (zůstává pro karty na mobilu). Má přednost před `sortable`.
   */
  sortKeys?: DataTableSortKey<T>[];
  width?: DataTableWidth;
  minWidth?: string;
  align?: DataTableAlign;
  /** Číselný sloupec: tabular-nums, zarovnání vpravo, nezalamovat, řazení number. */
  numeric?: boolean;
  nowrap?: boolean;
  /** Skrýt sloupec pod daným breakpointem (sm 576, md 768, lg 992 px). */
  hideBelow?: DataTableBreakpoint;
  /** Title hlavičky (vysvětlivka zkratky). */
  headerTitle?: string;
}

export interface DataTableSort {
  key: string;
  dir: DataTableSortDir;
}

export interface DataTableRowProps {
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Třída / styl / title řádku (např. zvýraznění vlastního řádku třídou "table-primary"). */
  rowProps?: (row: T) => DataTableRowProps;
  /** Detail pod řádkem; null/false/undefined = bez detailu. */
  expanded?: (row: T) => React.ReactNode;

  /** Řízené řazení (null = bez řazení). Bez něj si komponenta drží vlastní stav. */
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort) => void;
  /** Výchozí řazení v neřízeném režimu. */
  defaultSort?: DataTableSort;
  /** false = řádky už řadí rodič, komponenta jen ukazuje stav. Výchozí true. */
  sortRows?: boolean;

  /** Chování pod 768 px. Výchozí "scroll". */
  mobile?: DataTableMobile;
  /** Režim scroll: první sloupec přilepený vlevo při vodorovném posuvu. */
  stickyFirst?: boolean;
  /** Režim scroll: minimální šířka tabulky pod 768 px (např. "40rem"), aby se sloupce nemačkaly. */
  minWidth?: string;
  /** Režim cards: titulek karty. Bez něj se použije první sloupec. */
  cardTitle?: (row: T) => React.ReactNode;
  cardSubtitle?: (row: T) => React.ReactNode;
  /** Režim cards: klíče sloupců vypsaných jako „popisek: hodnota". Výchozí všechny sloupce. */
  cardFields?: string[];
  cardActions?: (row: T) => React.ReactNode;

  status?: DataTableStatus;
  emptyText?: string;
  loadingText?: string;
  dense?: boolean;
  /**
   * Hlavička přilepená nahoře; funguje od 768 px (na mobilu v režimu scroll má
   * přednost posuv). POZOR: od 768 px vypíná vodorovný posuv vnitřního obalu
   * (.table-responsive dostane overflow: visible, jinak by sticky nefungovalo),
   * takže tabulka musí být ve vlastním scroll kontejneru (overflow: auto) –
   * ten zároveň určuje --lks-table-sticky-top ("0px", nebo výšku vlastní pevné
   * hlavičky nad tabulkou). Bez takového obalu široká tabulka přeteče ze stránky.
   */
  stickyHeader?: boolean;
  caption?: string;
  footer?: React.ReactNode;
  className?: string;
}

/** Spojí třídy a vynechá prázdné hodnoty. */
const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(" ");

// ─────────────────────────────────────────────────────────────
//  Řazení
// ─────────────────────────────────────────────────────────────

const isBlank = (v: unknown): boolean =>
  v === null || v === undefined || v === "";

/** Tolerantní datum: Date, Firestore Timestamp (toDate / seconds), číslo ms, ISO, "D. M. YYYY". */
export function parseDateValue(v: unknown): number | null {
  if (isBlank(v)) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "object") {
    const o = v as { toDate?: () => Date; seconds?: number };
    if (typeof o.toDate === "function") {
      const d = o.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
    }
    if (typeof o.seconds === "number") return o.seconds * 1000;
    return null;
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    const m = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/.exec(s);
    if (m) {
      return new Date(
        Number(m[3]),
        Number(m[2]) - 1,
        Number(m[1]),
        m[4] ? Number(m[4]) : 0,
        m[5] ? Number(m[5]) : 0
      ).getTime();
    }
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Number(): snese českou čárku, mezery a "%" na konci. */
export function parseNumberValue(v: unknown): number | null {
  if (isBlank(v)) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") {
    const s = v.replace(/\s/g, "").replace(",", ".").replace(/%$/, "");
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const textValue = (v: unknown): string | null => {
  if (isBlank(v)) return null;
  const s = String(v).trim();
  return s ? s : null;
};

const compareText = (a: string, b: string): number =>
  a.localeCompare(b, "cs", { numeric: true, sensitivity: "base" });

/** Porovnání dvou hodnot daného typu; prázdné hodnoty jdou vždy na konec. */
export function compareSortValues(
  a: unknown,
  b: unknown,
  type: DataTableSortType,
  dir: DataTableSortDir
): number {
  let va: string | number | null;
  let vb: string | number | null;
  if (type === "number") {
    va = parseNumberValue(a);
    vb = parseNumberValue(b);
  } else if (type === "date") {
    va = parseDateValue(a);
    vb = parseDateValue(b);
  } else {
    va = textValue(a);
    vb = textValue(b);
  }
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const cmp =
    typeof va === "number" && typeof vb === "number"
      ? va - vb
      : compareText(String(va), String(vb));
  return dir === "asc" ? cmp : -cmp;
}

interface SortSpec<T> {
  type: DataTableSortType;
  getValue: (row: T) => unknown;
}

function valueByKey<T>(key: string): (row: T) => unknown {
  return (row: T) => (row as unknown as Record<string, unknown>)[key];
}

/** Jak řadit podle klíče: sloupec se stejným `key`, nebo jeden z jeho `sortKeys`. */
function resolveSortSpec<T>(
  columns: DataTableColumn<T>[],
  key: string
): SortSpec<T> | null {
  for (const column of columns) {
    const columnType: DataTableSortType =
      column.sortType || (column.numeric ? "number" : "text");
    const sub = column.sortKeys
      ? column.sortKeys.find((s) => s.key === key)
      : undefined;
    if (sub) {
      return {
        type: sub.sortType || columnType,
        getValue: sub.sortValue || valueByKey<T>(sub.key),
      };
    }
    if (column.key === key) {
      return {
        type: columnType,
        getValue: column.sortValue || valueByKey<T>(column.key),
      };
    }
  }
  return null;
}

function sortRowsBy<T>(
  rows: T[],
  spec: SortSpec<T> | null,
  dir: DataTableSortDir
): T[] {
  if (!spec) return rows;
  // Stabilní řazení: při shodě rozhoduje původní pořadí
  const indexed = rows.map((row, index) => ({
    row,
    index,
    value: spec.getValue(row),
  }));
  indexed.sort((x, y) => {
    const cmp = compareSortValues(x.value, y.value, spec.type, dir);
    return cmp !== 0 ? cmp : x.index - y.index;
  });
  return indexed.map((x) => x.row);
}

// ─────────────────────────────────────────────────────────────
//  Pomocné vykreslení
// ─────────────────────────────────────────────────────────────

const defaultCell = (value: unknown): React.ReactNode => {
  if (isBlank(value)) return "";
  if (typeof value === "boolean") return value ? "Ano" : "Ne";
  if (value instanceof Date) return value.toLocaleDateString("cs-CZ");
  if (typeof value === "object") {
    const o = value as { toDate?: () => Date };
    if (typeof o.toDate === "function") return o.toDate().toLocaleDateString("cs-CZ");
    return "";
  }
  return String(value);
};

function renderCell<T>(
  column: DataTableColumn<T>,
  row: T,
  index: number
): React.ReactNode {
  if (column.render) return column.render(row, index);
  return defaultCell((row as unknown as Record<string, unknown>)[column.key]);
}

function cellClass<T>(column: DataTableColumn<T>): string {
  return cx(
    "lks-table__cell",
    column.align && `lks-table__cell--${column.align}`,
    column.numeric && "lks-table__cell--numeric",
    (column.nowrap || column.width === "fit") && "lks-table__cell--nowrap",
    column.hideBelow && `lks-table__cell--hide-${column.hideBelow}`
  );
}

const colWidth = (width: DataTableWidth | undefined): string | undefined => {
  if (!width || width === "flex") return undefined;
  if (width === "fit") return "1%";
  return width;
};

/** Hodnota je skutečný obsah k vykreslení (ne null / undefined / false / ""). */
const hasContent = (node: React.ReactNode): boolean =>
  node !== null && node !== undefined && node !== false && node !== "";

/** Klik na tlačítko, odkaz nebo pole uvnitř řádku nespouští onRowClick. */
const isInteractiveTarget = (e: React.MouseEvent): boolean => {
  const target = e.target as HTMLElement | null;
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(
    target.closest("button, a, input, select, textarea, label, [data-lks-stop]")
  );
};

const SortIcon: React.FC<{ active: boolean; dir: DataTableSortDir }> = ({
  active,
  dir,
}) => (
  <i
    className={cx(
      "bi lks-table__sort-icon",
      !active && "bi-arrow-down-up",
      active && (dir === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill"),
      active && "lks-table__sort-icon--active"
    )}
    aria-hidden="true"
  />
);

// ─────────────────────────────────────────────────────────────
//  DataTable
// ─────────────────────────────────────────────────────────────

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowProps,
  expanded,
  sort,
  onSortChange,
  defaultSort,
  sortRows = true,
  mobile = "scroll",
  stickyFirst = false,
  minWidth,
  cardTitle,
  cardSubtitle,
  cardFields,
  cardActions,
  status = "idle",
  emptyText = "Žádné záznamy",
  loadingText = "Načítám…",
  dense = false,
  stickyHeader = false,
  caption,
  footer,
  className,
}: DataTableProps<T>): JSX.Element {
  const [innerSort, setInnerSort] = useState<DataTableSort | null>(
    defaultSort || null
  );
  const isControlled = sort !== undefined;
  const activeSort = isControlled ? sort : innerSort;

  const handleSort = (key: string) => {
    const next: DataTableSort =
      activeSort && activeSort.key === key
        ? { key, dir: activeSort.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" };
    if (!isControlled) setInnerSort(next);
    if (onSortChange) onSortChange(next);
  };

  const isActiveKey = (key: string): boolean =>
    Boolean(activeSort && activeSort.key === key);

  const sortedRows = useMemo(() => {
    if (!sortRows || !activeSort) return rows;
    return sortRowsBy(
      rows,
      resolveSortSpec(columns, activeSort.key),
      activeSort.dir
    );
  }, [rows, columns, activeSort, sortRows]);

  /** Tlačítko řazení v hlavičce (sloupec = jedno, sortKeys = jedno na klíč). */
  const renderSortButton = (
    key: string,
    label: React.ReactNode,
    title?: string
  ): React.ReactNode => {
    const active = isActiveKey(key);
    const dir: DataTableSortDir = active && activeSort ? activeSort.dir : "asc";
    return (
      <button
        type="button"
        className={cx("lks-table__sort", active && "lks-table__sort--active")}
        onClick={() => handleSort(key)}
        title={title}
      >
        <span className="lks-table__sort-label">{label}</span>
        <SortIcon active={active} dir={dir} />
      </button>
    );
  };

  const showState =
    status === "loading" || status === "empty" || rows.length === 0;
  const stateText = status === "loading" ? loadingText : emptyText;
  const stateNode = (
    <div className="lks-table__state" role="status" aria-live="polite">
      {status === "loading" && (
        <span
          className="spinner-border spinner-border-sm lks-table__spinner"
          aria-hidden="true"
        />
      )}
      {stateText}
    </div>
  );

  const clickable = Boolean(onRowClick);

  const rowHandlers = (row: T) =>
    clickable
      ? {
          tabIndex: 0,
          onClick: (e: React.MouseEvent) => {
            if (isInteractiveTarget(e)) return;
            if (onRowClick) onRowClick(row);
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (onRowClick) onRowClick(row);
            }
          },
        }
      : {};

  const wrapStyle = minWidth
    ? ({ "--lks-table-min": minWidth } as React.CSSProperties)
    : undefined;

  const table = (
    <div className="table-responsive lks-table__scroll" style={wrapStyle}>
      <table
        className={cx(
          "table lks-table",
          clickable && "table-hover",
          dense && "table-sm"
        )}
      >
        {caption && <caption className="lks-table__caption">{caption}</caption>}
        {/* Šířky nese jen th, záměrně bez colgroup: display: none na <col>
            prohlížeče ignorují, takže by sloupec skrytý přes hideBelow dál
            držel šířku z colgroup. */}
        <thead>
          <tr>
            {columns.map((column) => {
              const sortKeys =
                column.sortKeys && column.sortKeys.length > 0
                  ? column.sortKeys
                  : null;
              const active = sortKeys
                ? sortKeys.some((s) => isActiveKey(s.key))
                : isActiveKey(column.key);
              let ariaSort: "ascending" | "descending" | "none" | undefined;
              if (sortKeys || column.sortable) {
                ariaSort = "none";
                if (active && activeSort) {
                  ariaSort =
                    activeSort.dir === "asc" ? "ascending" : "descending";
                }
              }
              let header: React.ReactNode = column.header;
              if (sortKeys) {
                header = (
                  <span className="lks-table__sort-group">
                    {sortKeys.map((s, i) => (
                      <React.Fragment key={s.key}>
                        {i > 0 && (
                          <span
                            className="lks-table__sort-sep"
                            aria-hidden="true"
                          >
                            /
                          </span>
                        )}
                        {renderSortButton(s.key, s.label, s.title)}
                      </React.Fragment>
                    ))}
                  </span>
                );
              } else if (column.sortable) {
                header = renderSortButton(column.key, column.header);
              }
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={cx("lks-table__th", cellClass(column))}
                  style={{
                    width: colWidth(column.width),
                    minWidth: column.minWidth,
                  }}
                  title={column.headerTitle}
                  aria-sort={ariaSort}
                >
                  {header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {showState ? (
            <tr className="lks-table__state-row">
              <td colSpan={columns.length}>{stateNode}</td>
            </tr>
          ) : (
            sortedRows.map((row, index) => {
              const key = rowKey(row);
              const extra = rowProps ? rowProps(row) : undefined;
              const detail = expanded ? expanded(row) : null;
              return (
                <React.Fragment key={key}>
                  <tr
                    className={cx(
                      "lks-table__row",
                      clickable && "lks-table__row--clickable",
                      extra && extra.className
                    )}
                    style={extra ? extra.style : undefined}
                    title={extra ? extra.title : undefined}
                    {...rowHandlers(row)}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className={cellClass(column)}>
                        {renderCell(column, row, index)}
                      </td>
                    ))}
                  </tr>
                  {hasContent(detail) && (
                    <tr className="lks-table__expand">
                      <td colSpan={columns.length}>{detail}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  let cards: React.ReactNode = null;
  if (mobile === "cards") {
    const fieldColumns = cardFields
      ? cardFields
          .map((k) => columns.find((c) => c.key === k))
          .filter((c): c is DataTableColumn<T> => Boolean(c))
      : columns;
    const titleOf = (row: T, index: number): React.ReactNode => {
      if (cardTitle) return cardTitle(row);
      return columns.length ? renderCell(columns[0], row, index) : null;
    };

    cards = (
      <div className="lks-table__cards-wrap">
        {caption && <div className="lks-table__caption">{caption}</div>}
        {showState ? (
          stateNode
        ) : (
          <ul className="lks-table__cards">
            {sortedRows.map((row, index) => {
              const extra = rowProps ? rowProps(row) : undefined;
              const detail = expanded ? expanded(row) : null;
              const actions = cardActions ? cardActions(row) : null;
              const subtitle = cardSubtitle ? cardSubtitle(row) : null;
              return (
                <li
                  key={rowKey(row)}
                  className={cx(
                    "lks-table__card",
                    clickable && "lks-table__card--clickable",
                    extra && extra.className
                  )}
                  style={extra ? extra.style : undefined}
                  title={extra ? extra.title : undefined}
                  {...rowHandlers(row)}
                >
                  <div className="lks-table__card-title">{titleOf(row, index)}</div>
                  {hasContent(subtitle) && (
                    <div className="lks-table__card-sub">{subtitle}</div>
                  )}
                  {fieldColumns.length > 0 && (
                    <dl className="lks-table__card-fields">
                      {fieldColumns.map((column) => (
                        <React.Fragment key={column.key}>
                          <dt className="lks-table__card-dt" title={column.headerTitle}>
                            {column.header}
                          </dt>
                          <dd
                            className={cx(
                              "lks-table__card-dd",
                              column.numeric && "lks-table__cell--numeric"
                            )}
                          >
                            {renderCell(column, row, index)}
                          </dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  )}
                  {hasContent(detail) && (
                    <div className="lks-table__card-expand" data-lks-stop>{detail}</div>
                  )}
                  {hasContent(actions) && (
                    <div className="lks-table__card-actions">{actions}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div
      className={cx(
        "lks-table-wrap",
        `lks-table-wrap--${mobile}`,
        stickyFirst && "lks-table-wrap--sticky-first",
        stickyHeader && "lks-table-wrap--sticky-header",
        dense && "lks-table-wrap--dense",
        className
      )}
    >
      {table}
      {cards}
      {hasContent(footer) && <div className="lks-table__footer">{footer}</div>}
    </div>
  );
}

export default DataTable;
